import { useEffect, useState } from 'react'
import { Sparkles, Check, X, Edit3, RefreshCw } from 'lucide-react'
import { api } from '@/lib/api'

interface AiSuggestion {
  id: string
  draftsJson: string[]
  selectedDraftIdx: number | null
  confidenceScore: string | number | null
  inputTokens: number
  outputTokens: number
  status: 'PENDING' | 'APPROVED' | 'EDITED_AND_SENT' | 'REJECTED' | 'EXPIRED'
  createdAt: string
  botProfile: { id: string; name: string; mode: string; model: string }
}

interface Props {
  conversationId: string
  onApproved?: () => void
  refreshSignal?: number
}

export function AiSuggestionBar({ conversationId, onApproved, refreshSignal }: Props) {
  const [items, setItems] = useState<AiSuggestion[]>([])
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function load() {
    try {
      const r = await api<{ items: AiSuggestion[] }>(
        `/v1/marketing/bots/suggestions?conversationId=${conversationId}&status=PENDING&limit=5`,
      )
      setItems(r.items)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, refreshSignal])

  if (items.length === 0) return null

  async function approve(sug: AiSuggestion) {
    setBusy(true)
    setErr(null)
    try {
      const editedText = editing?.id === sug.id ? editing.text : undefined
      // Sprint 8: el backend toma el actor del JWT (req.user.sub). Ya no
      // enviamos userId en el body.
      await api(`/v1/marketing/bots/suggestions/${sug.id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ editedText }),
      })
      setEditing(null)
      await load()
      onApproved?.()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function reject(sug: AiSuggestion) {
    setBusy(true)
    setErr(null)
    try {
      await api(`/v1/marketing/bots/suggestions/${sug.id}/reject`, {
        method: 'POST',
      })
      setEditing(null)
      await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const top = items[0]!
  const draftFromSuggestion =
    typeof top.draftsJson?.[top.selectedDraftIdx ?? 0] === 'string'
      ? (top.draftsJson[top.selectedDraftIdx ?? 0] as string)
      : ''
  const draft = editing?.id === top.id ? editing.text : draftFromSuggestion
  const confidence = top.confidenceScore != null ? Number(top.confidenceScore) : null
  const confidenceLabel =
    confidence == null
      ? '—'
      : `${Math.round(confidence * 100)}%`
  const confidenceColor =
    confidence == null
      ? 'text-ink-700/50'
      : confidence >= 0.8
        ? 'text-emerald-700'
        : confidence >= 0.6
          ? 'text-amber-700'
          : 'text-red-700'

  return (
    <div className="border-t border-b border-blueprint-200 bg-blueprint-50 px-4 py-3">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="size-3.5 text-blueprint-700" />
        <span className="text-[10px] font-mono uppercase tracking-wider text-blueprint-700">
          sugerencia IA · {top.botProfile.name}
        </span>
        <span className="bp-pill bg-white text-ink-700">{top.botProfile.mode}</span>
        <span className={['text-[10px] font-mono ml-auto', confidenceColor].join(' ')}>
          confidence {confidenceLabel} · {top.inputTokens + top.outputTokens} tok ·{' '}
          {top.botProfile.model}
        </span>
        <button
          type="button"
          onClick={() => void load()}
          className="text-ink-700/50 hover:text-ink-900"
          title="Refrescar"
        >
          <RefreshCw className="size-3" />
        </button>
      </div>

      {editing?.id === top.id ? (
        <textarea
          value={editing.text}
          onChange={(e) => setEditing({ id: top.id, text: e.target.value })}
          rows={3}
          className="w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm bg-white"
        />
      ) : (
        <div className="bg-white border border-ink-900/10 rounded px-3 py-2 text-sm whitespace-pre-wrap">
          {draft}
        </div>
      )}

      {err && (
        <div className="mt-2 text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
          {err}
        </div>
      )}

      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => void approve(top)}
          className="bp-btn-primary"
          disabled={busy || draft.trim().length === 0}
        >
          <Check className="size-3" />
          {editing?.id === top.id ? 'Enviar editado' : 'Aceptar y enviar'}
        </button>
        {editing?.id !== top.id ? (
          <button
            type="button"
            onClick={() => setEditing({ id: top.id, text: draft })}
            className="bp-btn"
          >
            <Edit3 className="size-3" /> Editar
          </button>
        ) : (
          <button type="button" onClick={() => setEditing(null)} className="bp-btn">
            Cancelar edición
          </button>
        )}
        <button
          type="button"
          onClick={() => void reject(top)}
          className="bp-btn text-red-700 ml-auto"
          disabled={busy}
        >
          <X className="size-3" /> Rechazar
        </button>
        {items.length > 1 && (
          <span className="ml-2 text-[10px] font-mono text-ink-700/60">
            +{items.length - 1} en cola
          </span>
        )}
      </div>
    </div>
  )
}
